import React, { useState } from 'react';
import { motion } from 'framer-motion';
import logo from '../assets/logo.png';

const LoadingSpinner = ({ size = 'md', text = '', logoSpinner = false }) => {
  const [imageError, setImageError] = useState(false);
  // Size configurations
  const sizeConfig = {
    sm: { container: 'w-8 h-8', logo: 'w-6 h-6', ring: 'w-8 h-8', text: 'text-xs' },
    md: { container: 'w-12 h-12', logo: 'w-8 h-8', ring: 'w-12 h-12', text: 'text-sm' },
    lg: { container: 'w-16 h-16', logo: 'w-12 h-12', ring: 'w-16 h-16', text: 'text-base' },
    xl: { container: 'w-24 h-24', logo: 'w-16 h-16', ring: 'w-24 h-24', text: 'text-lg' }
  };

  const config = sizeConfig[size] || sizeConfig.md;

  if (logoSpinner) {
    // Logo-based spinner with rotating rings
    return (
      <div className="flex flex-col items-center justify-center gap-4">
        <div className={`relative ${config.container}`}>
          {/* Outer rotating ring */}
          <motion.div
            className={`${config.ring} rounded-full border-4 border-transparent border-t-[#2F80ED] border-r-[#90E0EF] absolute inset-0`}
            animate={{ rotate: 360 }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "linear"
            }}
          />
          
          {/* Inner rotating ring */}
          <motion.div
            className={`${config.ring} rounded-full border-4 border-transparent border-b-[#F9A826] border-l-[#27AE60] absolute inset-0`}
            animate={{ rotate: -360 }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "linear"
            }}
          />
          
          {/* Center logo with pulse animation */}
          <motion.div
            className={`${config.container} flex items-center justify-center absolute inset-0`}
            animate={{
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            {!imageError ? (
              <img 
                src={logo}
                alt="CVLogs" 
                className={`${config.logo} object-contain`}
                onError={() => setImageError(true)}
              />
            ) : (
              <div className={`${config.logo} flex items-center justify-center bg-[#2F80ED] rounded-full text-white font-bold`}>
                CV
              </div>
            )}
          </motion.div>
        </div>
        
        {text && (
          <motion.p
            className={`${config.text} font-medium text-[#2F80ED]`}
            animate={{
              opacity: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            {text}
          </motion.p>
        )}
      </div>
    );
  }

  // Simple modern spinner without logo
  return (
    <div className="flex flex-col items-center justify-center gap-3">
      <div className="relative">
        {/* Gradient spinning ring */}
        <motion.div
          className={`${config.ring} rounded-full`}
          style={{
            background: 'conic-gradient(from 0deg, #2F80ED, #90E0EF, #F9A826, #27AE60, #2F80ED)',
            padding: '3px',
          }}
          animate={{ rotate: 360 }}
          transition={{
            duration: 1.2,
            repeat: Infinity,
            ease: "linear"
          }}
        >
          <div className={`${config.ring} rounded-full bg-white`} />
        </motion.div>
      </div>
      
      {text && (
        <motion.p
          className={`${config.text} font-medium text-[#6B7280]`}
          animate={{
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          {text}
        </motion.p>
      )}
    </div>
  );
};

export default LoadingSpinner;
